import React, { useCallback, useEffect, useState } from "react";
import { LanguageIcon } from "src/molecules/NavbarIcons/styles";
import Logo from "src/atoms/TopconLogo";
import Input from "src/atoms/Input";
import { LoginMessages } from "src/languages/interfaces";
import api from "src/store/services/rest";
import { PublicClientApplication } from "@azure/msal-browser";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { b2cPolicies } from "src/authConfig";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { ERoutes } from "src/helpers/utils";
import * as Styled from "./styles";

export interface StandardReturnObject<T> {
  status: "success" | "alert";
  message: string;
  result?: T
  tokenB2c: string
}

export interface AuthStateResponse {
  user: User;
  params: Param[];
}

export interface User {
  id_user: string;
  password: string;
  passwordConfirm?: string;
  name: string;
  salt: string;
  registration_date: Date;
  updated_date: Date;
  activation_date: Date;
  last_access: Date;
  active: boolean;
  admin: boolean;
  super_admin: boolean;
  email: string;
  sendMail: boolean;
  current_survey: { external_id: string; show: boolean };
  params: Param[];
  allowed_change_params: boolean;
  allowed_change_vehicles: boolean;
  allowed_change_drivers: boolean;
  allowed_change_locations: boolean;
  allowed_change_zones: boolean;
}

export interface Param {
  id_param?: string;
  name?: string;
  key?: string;
  registration_date?: Date;
  updated_date?: Date;
}

const Sso: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const { t } = useTranslation();
  const { instance } = useMsal();

  const handleLogin = useCallback(async (emailValue: string) => {
    if (!emailValue) {
      addToast({ type: "error", title: t(LoginMessages.loginRequiredEmail) });

      return;
    }

    try {
      setIsLoading(true);

      const domain = emailValue.split("@")[1];
      const response = await api.post("/auth/GetSSOParamByDomain", { domain });
      const responseData = response.data;

      if (!responseData.useSso) {
        const signInFlowRequest = {
          authority: b2cPolicies.authorities.signIn.authority,
          scopes: [],
          extraQueryParameters: { login_hint: emailValue }
        };

        await instance.loginRedirect(signInFlowRequest);

        return;
      }

      const idClient = responseData.clientId;
      const idTenant = responseData.tenantId;
      const idTenantRegTenant = responseData.tenantIdRegTenant;

      const msalInstance = new PublicClientApplication({
        auth: {
          clientId: idClient,
          authority: `https://login.microsoftonline.com/${idTenant}`,
          redirectUri: window.location.origin
        }
      });

      try {
        await msalInstance.initialize();
      } catch {
        msalInstance.setActiveAccount(null);
        addToast({ type: "error", title: t(LoginMessages.loginFailed) });

        return;
      }

      msalInstance.setActiveAccount(null);

      let loginResponse;

      try {
        loginResponse = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "User.Read"],
          loginHint: emailValue
        });
      } catch {
        addToast({ type: "error", title: t(LoginMessages.loginFailed) });

        return;
      }

      msalInstance.setActiveAccount(loginResponse.account);

      const { data }: { data: StandardReturnObject<AuthStateResponse> } = await api.post("auth/loginWithSSO", {
        name: loginResponse.account.name,
        email: loginResponse.account.username,
        externalId: loginResponse.account.homeAccountId,
        tenantId: idTenant,
        clientId: idClient,
        tokenFromMicrosoftPopup: loginResponse.idToken,
        tenantIdRegTenant: idTenantRegTenant
      });

      if (data.status === "success") {
        localStorage.setItem("@Topcon:token", data.tokenB2c || "");
        navigate(0);

        return;
      }

      addToast({ type: "error", title: t(LoginMessages.ssoLoginError), description: "" });
    } catch (error) {
      const description = error instanceof Error ? error.message : String(error);

      addToast({ type: "error", title: t(LoginMessages.internalClientError), description, overwrite: true });
    } finally {
      setIsLoading(false);
    }
  }, [addToast, navigate, t, instance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLogin(email);
  };

  useEffect(() => {
    if (instance.getActiveAccount() && localStorage.getItem("@Topcon:token")) {
      navigate(ERoutes.home);
    }
  }, []);

  return (
    <>
      <AuthenticatedTemplate>
        <Navigate to="/" />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Styled.LoginWrap>
          <Styled.DiagGold />
          <Styled.DiagConcrete />
          <Styled.DiagBlue />
          <Styled.LoginFormArea>
            <Styled.LoginFormInner>
              <Logo />
              <form onSubmit={handleSubmit}>
                <Input
                  name="email"
                  label={t(LoginMessages.loginEmailLabel)}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Styled.BtnRow>
                  <Button
                    text={t(LoginMessages.loginLoginButton)}
                    type="submit"
                    size="large"
                    loading={isLoading}
                    disabled={!email.trim()}
                    fullWidth
                  />
                </Styled.BtnRow>
              </form>
            </Styled.LoginFormInner>
          </Styled.LoginFormArea>
          <LanguageIcon />
        </Styled.LoginWrap>
      </UnauthenticatedTemplate>
    </>
  );
};

export default Sso;
